import { ApiClient } from '@api/api-configuration';
import {
  BillingEntityTypeEnum,
  BillingTransactionTypeEnum,
  type CustomerProfileEntry,
  type CustomerProfileResponse,
} from '@api/READ_ONLY/billing_api/Api';
import type { CaregiverMinified } from '@api/READ_ONLY/caregiver_api/Api';
import type { Client } from '@api/READ_ONLY/client_api/Api';
import type { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LedgerRow } from './CustomerProfileLedgerTable';

export type EntityType = BillingEntityTypeEnum;

const CUSTOMER_PROFILE_QUERY_PARAM_KEYS = {
  ID: 'id',
  TYPE: 'type',
} as const;

const ENTITY_TYPE_VALUES = {
  CLIENT: BillingEntityTypeEnum.Client,
  DOCTOR: BillingEntityTypeEnum.Doctor,
  CAREGIVER: BillingEntityTypeEnum.Caregiver,
  CAREGIVER_CASH_REGISTER: BillingEntityTypeEnum.CaregiverCashRegister,
} as const;

const normalizeEntityTypeParam = (value: string | null): EntityType => {
  if (!value) {
    return ENTITY_TYPE_VALUES.CLIENT;
  }

  const normalized = value.trim().toLowerCase();

  if (
    normalized === ENTITY_TYPE_VALUES.CLIENT ||
    normalized === 'clients' ||
    normalized === 'customer' ||
    normalized === 'customers'
  ) {
    return ENTITY_TYPE_VALUES.CLIENT;
  }

  if (normalized === ENTITY_TYPE_VALUES.DOCTOR || normalized === 'doctors') {
    return ENTITY_TYPE_VALUES.DOCTOR;
  }

  if (
    normalized === ENTITY_TYPE_VALUES.CAREGIVER ||
    normalized === 'caregivers' ||
    normalized === 'nurse' ||
    normalized === 'nurses'
  ) {
    return ENTITY_TYPE_VALUES.CAREGIVER;
  }

  if (
    normalized === ENTITY_TYPE_VALUES.CAREGIVER_CASH_REGISTER ||
    normalized === 'cash' ||
    normalized === 'cash-register'
  ) {
    return ENTITY_TYPE_VALUES.CAREGIVER_CASH_REGISTER;
  }

  return ENTITY_TYPE_VALUES.CLIENT;
};

const parseEntityIdentifierParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
};

type RecordType = BillingTransactionTypeEnum;

const mapRecordType = (recordType: string): RecordType => {
  const enumValues = Object.values(BillingTransactionTypeEnum);
  return (
    enumValues.includes(recordType as RecordType) ? recordType : BillingTransactionTypeEnum.ClientPayment
  ) as RecordType;
};

export const useCustomerProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const queryClient = useQueryClient();

  const initialEntityType = normalizeEntityTypeParam(searchParams.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.TYPE));
  const initialEntityIdentifier = parseEntityIdentifierParam(searchParams.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID));

  const [entityType, setEntityType] = useState<EntityType>(initialEntityType);
  const [entityIdentifier, setEntityIdentifier] = useState<number | undefined>(initialEntityIdentifier);
  const [startingDate, setStartingDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endingDate, setEndingDate] = useState<Dayjs | null>(dayjs());
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [paginationLimit, setPaginationLimit] = useState(10);
  const [editingPaymentId, setEditingPaymentId] = useState<number | null>(null);
  const [editingEntityIdentifier, setEditingEntityIdentifier] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(searchParamsKey);
    const nextEntityType = normalizeEntityTypeParam(params.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.TYPE));
    const nextEntityIdentifier = parseEntityIdentifierParam(params.get(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID));

    setEntityType(nextEntityType);
    setEntityIdentifier(nextEntityIdentifier);
    setPaginationOffset(0);
  }, [searchParamsKey]);

  const selectedClientQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CLIENT, entityIdentifier],
    enabled: entityType === ENTITY_TYPE_VALUES.CLIENT && Boolean(entityIdentifier),
    queryFn: async (): Promise<Client | null> => {
      const response = await ApiClient.clientApi.clients.getClientById(entityIdentifier as number);
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const selectedDoctorQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_DOCTOR, entityIdentifier],
    enabled: entityType === ENTITY_TYPE_VALUES.DOCTOR && Boolean(entityIdentifier),
    queryFn: async (): Promise<Doctor | null> => {
      const response = await ApiClient.doctorsApi.doctors.getDoctorById(entityIdentifier as number);
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // caregiver_cash_register accounts are keyed by the caregiver id, so the selected
  // entity for that type is fetched as a caregiver, reusing the caregiver-by-id query.
  const isCaregiverBackedEntity =
    entityType === ENTITY_TYPE_VALUES.CAREGIVER || entityType === ENTITY_TYPE_VALUES.CAREGIVER_CASH_REGISTER;

  const selectedCaregiverQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.PAYMENT_FORM_CAREGIVER, entityIdentifier],
    enabled: isCaregiverBackedEntity && Boolean(entityIdentifier),
    queryFn: async (): Promise<CaregiverMinified | null> => {
      const response = await ApiClient.caregiverApi.caregivers.getCaregiverById(entityIdentifier as number);
      return (response.data as CaregiverMinified) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  const canFetchCustomerProfile = typeof entityIdentifier === 'number' && entityIdentifier > 0;

  const paymentsQuery = useQuery<CustomerProfileResponse>({
    queryKey: [
      BILLING_QUERY_KEYS.CUSTOMER_PROFILE_PAYMENTS,
      entityType,
      entityIdentifier,
      startingDate?.toISOString() ?? null,
      endingDate?.toISOString() ?? null,
      paginationOffset,
      paginationLimit,
    ],
    enabled: canFetchCustomerProfile,
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.getCustomerProfile({
        entityType,
        entityId: entityIdentifier ?? null,
        startAt: startingDate ? startingDate.startOf('day').toISOString() : null,
        endAt: endingDate ? endingDate.endOf('day').toISOString() : null,
        offset: paginationOffset,
        limit: paginationLimit,
      });

      return response.data;
    },
  });

  const paymentsResponse: CustomerProfileResponse = paymentsQuery.data ?? {
    total: 0,
    offset: paginationOffset,
    limit: paginationLimit,
    data: [],
  };

  const ledgerRows: LedgerRow[] = useMemo(
    () =>
      (paymentsResponse.data ?? []).map((entry: CustomerProfileEntry) => ({
        id: entry.identifier ?? String(entry.recordId),
        paymentId: entry.recordId,
        paymentIdentifier: entry.identifier ?? String(entry.recordId),
        entityIdentifier: entry.entityId,
        entityName: entry.entityName ?? null,
        date: entry.date,
        visitId: entry.visitId ?? null,
        recordType: mapRecordType(entry.recordType),
        kind: entry.kind,
        notes: entry.notes ?? '',
        amount: entry.amount,
        runningTotal: entry?.runningTotal ?? null,
      })),
    [paymentsResponse.data]
  );

  const currentBalanceTotal = useMemo(() => ledgerRows.reduce((sum, row) => sum + row.amount, 0), [ledgerRows]);

  const startingBillingPeriod = paymentsResponse.total ? paymentsResponse.offset + 1 : 0;

  const endingBillingPeriod = paymentsResponse.total
    ? Math.min(paymentsResponse.offset + paymentsResponse.limit, paymentsResponse.total)
    : 0;

  const currentPageNumber = Math.floor(paginationOffset / paginationLimit) + 1;

  const resetPaginationOffset = () => setPaginationOffset(0);

  const updateEntitySearchParams = (nextType: EntityType, nextIdentifier?: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.set(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.TYPE, nextType);

    if (typeof nextIdentifier === 'number' && nextIdentifier > 0) {
      nextSearchParams.set(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID, String(nextIdentifier));
    } else {
      nextSearchParams.delete(CUSTOMER_PROFILE_QUERY_PARAM_KEYS.ID);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const changeEntityType = (nextType: EntityType) => {
    updateEntitySearchParams(nextType);
    resetPaginationOffset();
  };

  const changeEntityIdentifier = (nextIdentifier: number | undefined) => {
    updateEntitySearchParams(entityType, nextIdentifier);
    resetPaginationOffset();
  };

  const changeStartingDate = (nextDate: Dayjs | null) => {
    setStartingDate(nextDate);
    resetPaginationOffset();
  };

  const changeEndingDate = (nextDate: Dayjs | null) => {
    setEndingDate(nextDate);
    resetPaginationOffset();
  };

  const openEditModal = (paymentId: number, entityIdentifier: number) => {
    setEditingPaymentId(paymentId);
    setEditingEntityIdentifier(entityIdentifier);
  };

  const closeEditModal = () => {
    setEditingPaymentId(null);
    setEditingEntityIdentifier(null);
  };

  const deletePaymentMutation = useMutation({
    mutationFn: async (paymentId: number) => {
      await ApiClient.billingApi.billing.deleteTransaction(paymentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.CUSTOMER_PROFILE_PAYMENTS] });
      queryClient.invalidateQueries({ queryKey: [BILLING_QUERY_KEYS.RECENT_PAYMENTS_SIDEBAR] });
    },
  });

  return {
    entityType,
    entityIdentifier,
    startingDate,
    endingDate,
    ledgerRows,
    paymentsResponse,
    currentBalanceTotal,
    startingBillingPeriod,
    endingBillingPeriod,
    currentPageNumber,
    paginationLimit,
    editingPaymentId,
    editingEntityIdentifier,
    selectedClient:
      entityType === ENTITY_TYPE_VALUES.CLIENT && entityIdentifier
        ? (selectedClientQuery.data ?? undefined)
        : undefined,
    selectedDoctor:
      entityType === ENTITY_TYPE_VALUES.DOCTOR && entityIdentifier
        ? (selectedDoctorQuery.data ?? undefined)
        : undefined,
    selectedCaregiver:
      isCaregiverBackedEntity && entityIdentifier ? (selectedCaregiverQuery.data ?? undefined) : undefined,
    isLoadingPayments: paymentsQuery.isFetching,
    changeEntityType,
    changeEntityIdentifier,
    changeStartingDate,
    changeEndingDate,
    setPaginationOffset,
    setPaginationLimit,
    openEditModal,
    closeEditModal,
    deletePaymentById: deletePaymentMutation.mutate,
    isDeletingPayment: deletePaymentMutation.isPending,
  };
};
